import type { SpanRecord } from '@apm/contracts';
import { useMemo, useState } from 'react';
import { TIP } from '../terms';
import { formatCount, formatDuration } from '../time';
import { Button, Cell, type Column, EmptyState, Row, Table, Toggle, Tooltip } from '../ui';
import {
  applyParams,
  groupByQuery,
  type SqlItems,
  splitSql,
  sqlVerb,
  truncationHint,
} from './timeline';

/**
 * 요청 안의 DB 조회 전체 목록 (7회차 사용자 요구).
 *
 * 실패한 쿼리만이 아니라 **성공한 조회까지 전부** 순서대로 보인다.
 * 문장은 기본으로 접어 둔다. 한 요청에 쿼리가 수십 개면 전부 펼친 화면은 읽을 수 없다.
 *
 * 19회차에 셋이 붙었다.
 *   - **넘긴 값 보기** (2026-09-07 결정 7): `$1`·`$2` 자리에 실제 값을 끼워 보인다.
 *     "왜 아무것도 못 찾았나"는 값을 봐야 안다. 원문은 토글로 되돌린다
 *   - **같은 문장 묶어 보기** (후보 #9): 같은 조회가 수십 번 돈 것을 한 줄로 접는다. N+1을 찾는 길
 *   - 목록 높이 고정 해제 (후보 #13): 스무 줄짜리 요청이 표 안에서만 스크롤되지 않는다
 *
 * 22-B회차에 둘이 더 붙었다.
 *   - **트랜잭션 제어 접기** (2026-09-08 사용자 확정 4): `START TRANSACTION`·`COMMIT` 같은 문장은
 *     DB 왕복이라 남기되 기본으로 숨긴다. 머리글이 "쿼리 20 · 트랜잭션 제어 8"로 나뉜다.
 *     시간축(SpanBars)에는 그대로 둔다 — 경계가 보여야 BEGIN→COMMIT 시간을 잰다
 *   - **잘린 문장 표시** (UI 피드백 3): 에이전트 상한에 잘린 문장 끝에 그 사실을 적는다
 *
 * 23-A회차에 **세션 설정 접기**가 같은 방식으로 붙었다 (2026-09-08 사용자 확정 22).
 * OneWorks는 트랜잭션마다 `SELECT set_config('app.tenant_id', $1, true)`를 던져(멀티테넌트 RLS)
 * 목록의 절반이 그 한 문장이 된다. 값이 늘 같고 문제의 원인인 적이 없어 따로 세고 접는다.
 * 트랜잭션 제어와 따로 세는 이유는 접는 까닭이 달라서다 — 하나는 경계, 하나는 설정이다.
 *
 * 목록 높이는 23-A회차부터 `Table`의 기본 상한을 따른다(UI 피드백 20). 19회차에 상한을 없앴지만
 * 쿼리가 수십 개인 요청에서 아래 구획이 저 밑으로 밀려 되돌렸다.
 */
const COLUMNS: readonly Column[] = [
  { label: '#', width: 'w-12' },
  { label: '종류', width: 'w-24' },
  { label: '이름' },
  { label: '소요', width: 'w-24' },
  { label: '상태', width: 'w-24' },
  { label: 'DB 오류 코드', width: 'w-32', title: TIP.sqlErrorCode },
  { label: 'SQL', width: 'w-32' },
];

const GROUP_COLUMNS: readonly Column[] = [
  { label: '횟수', width: 'w-20' },
  { label: '종류', width: 'w-24' },
  { label: 'SQL' },
  { label: '합계', width: 'w-24' },
  { label: '최대', width: 'w-24' },
  { label: 'DB 오류', width: 'w-24' },
];

export function SqlTable({ spans }: { spans: readonly SpanRecord[] }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [allOpen, setAllOpen] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [rawSql, setRawSql] = useState(false);
  const [showControl, setShowControl] = useState(false);
  const [showSession, setShowSession] = useState(false);

  const toggle = (spanId: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(spanId)) next.delete(spanId);
      else next.add(spanId);
      return next;
    });
  };

  const toggleAll = () => {
    setAllOpen((prev) => !prev);
    setOpen(new Set());
  };

  const isOpen = (spanId: string) => allOpen !== open.has(spanId);
  const split = useMemo(() => splitSql(spans), [spans]);
  // 접혀 있으면 그 문장들은 목록·묶음 어디에도 안 보인다. 숨긴 건수만 말한다.
  // 펼친 것을 도로 섞을 때는 원래 자리(index)로 세워 순서를 지킨다
  const listed = useMemo(() => {
    const rows = [...split.queries];
    if (showControl) rows.push(...split.control);
    if (showSession) rows.push(...split.session);
    return rows.sort((a, b) => a.index - b.index);
  }, [split, showControl, showSession]);
  const failed = spans.filter((span) => span.status === 'error').length;
  const groups = useMemo(() => groupByQuery(listed.map((item) => item.span)), [listed]);
  const repeated = groups.filter((group) => group.count > 1).length;

  if (spans.length === 0) {
    return <EmptyState title="이 요청에는 SQL이 없다" />;
  }

  const text = (span: SpanRecord): string =>
    rawSql || span.query === undefined ? (span.query ?? '') : applyParams(span.query, span.params);

  return (
    <div className="space-y-3">
      <div className="tabular flex flex-wrap items-center justify-between gap-4 text-xs text-muted">
        <span>
          {split.control.length > 0 || split.session.length > 0 ? (
            <>
              쿼리 {formatCount(split.queries.length)}
              {split.control.length > 0
                ? ` · 트랜잭션 제어 ${formatCount(split.control.length)}`
                : ''}
              {split.session.length > 0 ? ` · 세션 설정 ${formatCount(split.session.length)}` : ''}
            </>
          ) : (
            <>{formatCount(spans.length)}건</>
          )}
          {failed > 0 ? <span className="ml-2 text-error">DB 오류 {failed}건</span> : null}
          {repeated > 0 ? <span className="ml-2 text-fail">반복된 문장 {repeated}가지</span> : null}
        </span>
        <span className="flex flex-wrap items-center gap-4">
          <Toggle
            id="sql-grouped"
            checked={grouped}
            onChange={setGrouped}
            label="같은 문장 묶어 보기"
            hint="같은 쿼리가 반복되면 N+1 문제일 수 있다"
          />
          <Toggle
            id="sql-raw"
            checked={rawSql}
            onChange={setRawSql}
            label="파라미터 대신 원문"
            hint="$1·$2 자리에 실제 파라미터 값을 끼운 것이 기본이다"
          />
          {grouped ? null : (
            <Button size="sm" onClick={toggleAll} ariaExpanded={allOpen}>
              {allOpen ? '쿼리 전체 접기' : '쿼리 전체 펼치기'}
            </Button>
          )}
        </span>
      </div>

      <FoldedNote
        label="트랜잭션 제어"
        items={split.control}
        shown={showControl}
        onToggle={() => setShowControl((prev) => !prev)}
        example="(BEGIN·COMMIT 등)"
        tip={
          'START TRANSACTION·BEGIN·COMMIT·ROLLBACK·SAVEPOINT·RELEASE.\n' +
          'DB 왕복이지만 문제의 원인인 적은 없어 접어 둔다.\n시간축에는 그대로 있다'
        }
      />
      <FoldedNote
        label="세션 설정"
        items={split.session}
        shown={showSession}
        onToggle={() => setShowSession((prev) => !prev)}
        example="(set_config 등)"
        tip={
          'SET·RESET·SHOW·SELECT set_config(·SELECT current_setting(.\n' +
          'OneWorks는 트랜잭션마다 회사 구분 값을 세션에 넣는다. 값이 늘 같아 접어 둔다.\n' +
          '시간축에는 그대로 있다'
        }
      />

      {grouped ? (
        <Table columns={GROUP_COLUMNS} caption="같은 문장끼리 묶은 SQL" minWidth="min-w-[820px]">
          {groups.map((group) => (
            <Row key={group.query} tone={group.errorCount > 0 ? 'error' : undefined}>
              <Cell nowrap className="tabular">
                {group.count > 1 ? (
                  <span className="font-semibold text-fail">{group.count}회</span>
                ) : (
                  <span>{group.count}회</span>
                )}
              </Cell>
              <Cell mono>{sqlVerb(group.query)}</Cell>
              <Cell mono className="max-w-[520px] truncate" title={group.query}>
                {group.query}
              </Cell>
              <Cell mono nowrap className="tabular">
                {formatDuration(group.totalMs)}
              </Cell>
              <Cell mono nowrap className="tabular">
                {formatDuration(group.maxMs)}
              </Cell>
              <Cell nowrap tone={group.errorCount > 0 ? 'error' : 'muted'} className="tabular">
                {group.errorCount > 0 ? `${group.errorCount}건` : '-'}
              </Cell>
            </Row>
          ))}
        </Table>
      ) : (
        <Table columns={COLUMNS} caption="이 요청이 실행한 SQL" minWidth="min-w-[760px]">
          {listed.map(({ span, index }) => {
            const error = span.status === 'error';
            const shown = isOpen(span.spanId);
            const hint = truncationHint(span);
            return [
              <Row key={span.spanId} tone={error ? 'error' : undefined}>
                <Cell tone="muted" className="tabular">
                  {index}
                </Cell>
                <Cell mono>{sqlVerb(span.query)}</Cell>
                <Cell mono tone="muted" className="max-w-[280px] truncate" title={span.name}>
                  {span.name}
                </Cell>
                <Cell mono nowrap className="tabular">
                  {formatDuration(span.durationMs)}
                </Cell>
                <Cell nowrap tone={error ? 'error' : 'muted'}>
                  {error ? '실패' : '성공'}
                </Cell>
                <Cell mono tone="error" className="tabular">
                  {span.errorCode ?? ''}
                </Cell>
                <Cell>
                  {span.query === undefined ? (
                    <span className="text-muted">없음</span>
                  ) : (
                    <Button variant="link" onClick={() => toggle(span.spanId)} ariaExpanded={shown}>
                      {shown ? '접기' : '쿼리 보기'}
                    </Button>
                  )}
                </Cell>
              </Row>,
              shown && span.query !== undefined ? (
                <tr key={`${span.spanId}-query`} className="border-b border-hair">
                  <td colSpan={COLUMNS.length} className="px-4 pb-3">
                    <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-band p-3 font-mono text-xs leading-relaxed text-ink">
                      {text(span)}
                      {hint !== undefined ? (
                        <span className="ml-1 font-sans text-fail" title={TIP.sqlTruncated}>
                          {hint}
                        </span>
                      ) : null}
                    </pre>
                    {!rawSql && span.params !== undefined && span.params.length > 0 ? (
                      <p className="mt-1 text-xs text-muted">
                        <code className="font-mono">$1</code>부터 순서대로 파라미터{' '}
                        {span.params.length}개를 끼워 넣었다
                      </p>
                    ) : null}
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </Table>
      )}

      <p className="text-xs text-muted">
        <Tooltip text="요청·응답 본문, 인증 헤더, URL의 서명 토큰은 앱을 떠나기 전에 지운다">
          SQL 문장과 파라미터 값은 사내 관제용으로 그대로 저장한다
        </Tooltip>
      </p>
    </div>
  );
}

/**
 * 접어 둔 문장 묶음 한 줄 (23-A회차). 트랜잭션 제어와 세션 설정이 같은 모양을 쓴다.
 * 없으면 줄 자체를 그리지 않는다 — 0건짜리 안내가 두 줄 붙어 있으면 목록이 뒤로 밀린다.
 */
function FoldedNote({
  label,
  items,
  shown,
  onToggle,
  example,
  tip,
}: {
  label: string;
  items: SqlItems;
  shown: boolean;
  onToggle: () => void;
  /** 어떤 문장인지 한눈에 보여 주는 짧은 예 */
  example: string;
  tip: string;
}) {
  if (items.length === 0) return null;
  const failed = items.filter((item) => item.span.status === 'error').length;
  return (
    <p className="tabular flex flex-wrap items-center gap-2 rounded-lg border border-hair bg-panel px-3 py-2 text-xs text-muted">
      <span>
        {label} {formatCount(items.length)}건{shown ? ' 표시 중' : ' 숨김'}
        {failed > 0 ? <span className="ml-2 text-error">그중 실패 {failed}건</span> : null}
      </span>
      <Tooltip text={tip}>
        <span className="text-muted">{example}</span>
      </Tooltip>
      <Button variant="link" onClick={onToggle} ariaExpanded={shown}>
        {shown ? '다시 숨기기' : '펼치기'}
      </Button>
    </p>
  );
}
